import React, { useState, useEffect, useContext } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import GlobalStyle from './styles/GlobalStyle';
import LandingPage from './components/LandingPage';
import Dashboard from './components/Dashboard/Dashboard';
import Subscriptions from './components/Subscriptions/Subscriptions';
import PaymentMethods from './components/PaymentMethods/PaymentMethods';
import Tags from './components/Tags/Tags';
import Login from './components/Auth/Login';
import SignUp from './components/Auth/SignUp';
import ForgotPassword from './components/Auth/ForgotPassword';
import { ThemeProvider } from './context/ThemeContext';
import CreateSubscription from './components/Subscriptions/CreateSubscription';
import CreatePaymentMethod from './components/PaymentMethods/CreatePaymentMethod';
import CreateTag from './components/Tags/CreateTag';
import ProSubscription from './components/Subscription/ProSubscription';
import PaymentDetails from './components/Subscription/PaymentDetails';
import FreeSubscription from './components/Subscription/FreeSubscription';
import { useNavigate } from 'react-router-dom';
import supabase from './config/supabaseClient';
import { AuthContext } from './contexts/AuthContext';
import EditSubscription from './components/Subscriptions/EditSubscription';
import CalendarPage from './pages/CalendarPage';
import SubscriptionAlerts from './pages/SubscriptionAlerts';

const ProtectedRoute = ({ children }) => {
  const { user } = React.useContext(AuthContext);
  return user ? children : <Navigate to="/login" />;
};

const App = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check active sessions and sets the user
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      setLoading(false);
    });

    // Listen for changes on auth state (login, logout, etc)
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
    });

    return () => subscription.unsubscribe();
  }, []);

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <AuthContext.Provider value={{ user, setUser }}>
      <ThemeProvider>
        <GlobalStyle />
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<LandingPage title="Prenewal" />} />
            <Route path="/login" element={<Login title="Login | Prenewal" />} />
            <Route path="/signup" element={<SignUp title="Sign Up | Prenewal" />} />
            <Route
              path="/dashboard/*"
              element={user ? <Dashboard title="Dashboard | Prenewal" /> : <Navigate to="/login" />}
            />
            <Route
              path="/subscriptions/*"
              element={user ? <Subscriptions title="Subscriptions | Prenewal" /> : <Navigate to="/login" />}
            />
            <Route path="/payment-methods" element={<PaymentMethods title="Payment Methods | Prenewal" />} />
            <Route path="/tags" element={<Tags title="Tags | Prenewal" />} />
            <Route path="/forgot-password" element={<ForgotPassword title="Forgot Password | Prenewal" />} />
            <Route path="/subscriptions/create" element={<CreateSubscription title="New Subscription | Prenewal" />} />
            <Route path="/payment-methods/create" element={<CreatePaymentMethod title="New Payment Method | Prenewal" />} />
            <Route path="/tags/create" element={<CreateTag title="New Tag | Prenewal" />} />
            <Route path="/pro-subscription" element={<ProSubscription title="Pro Subscription | Prenewal" />} />
            <Route path="/payment-details" element={<PaymentDetails title="Payment Details | Prenewal" />} />
            <Route path="/free-subscription" element={<FreeSubscription title="Free Subscription | Prenewal" />} />
            <Route 
              path="/subscriptions/edit/:id" 
              element={user ? <EditSubscription title="Edit Subscription | Prenewal" /> : <Navigate to="/login" />} 
            />
            <Route path="/calendar" element={<CalendarPage title="Calendar | Prenewal" />} />
            <Route 
              path="/subscriptions/alerts" 
              element={user ? <SubscriptionAlerts title="Subscription Alerts | Prenewal" /> : <Navigate to="/login" />}
            />
          </Routes>
        </BrowserRouter>
      </ThemeProvider>
    </AuthContext.Provider>
  );
};

export default App;
